'use client';

import styles from './plancard.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
    ScrollableChannelTiles,
    ImageItem,
    ScrollableChannelTilesHandle,
} from '@/ui-components/molecules/ScrollableChannelTiles/ScrollableChannelTiles';
import { Button, ButtonProps } from '@/ui-components/atoms/Button/Button';
import { StarAnimation } from '@/ui-components/atoms/StarAnimation/StarAnimation';
import { Modal } from '@/ui-components/molecules/Modal/Modal';
import { PopularChannelsModal } from '../PopularChannelsModal/PopularChannelsModal';
import { Text } from '@/ui-components/atoms/Text/Text';
import { colorBrightness } from '@/packages/color/colorBrightness';
import { ChannelBadge } from '../ChannelCardStrip/ChannelBadge';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { ShowMore } from '../ShowMore/ShowMore';

const {
    cardHeading,
    planCard,
    headingSection,
    cardEyebrow,
    optionalItemWrapper,
    optionalItems,
    optionalItemHeading,
    optionalItemSubheading,
    toggleContainer,
    switchContainer,
    switchToggle,
    switchOn,
    circle,
    planPriceWrapper,
    badgeStyle,
    planCardFlag,
    whiteText,
    cardDescription,
    addOnLabel,
    bannerHidden,
} = styles;

export interface PlanCardProps {
    planCardContent: PlanCardContent;
    index: number;
    allAccessPriceText?: React.ReactNode;
}

type OfferChannels = {
    name: string;
    popularChannels: ImageItem[];
    extraTileColor: string;
    extraTileLabel: string;
    channelsLinkLabel: string;
    channels: {
        title: string;
        tabs: {
            title: string;
            description?: string;
            items: ImageItem[];
            mediaImage?: string;
        }[];
    };
};

interface BuildPlanOfferKeys {
    offer1Label?: string;
    offer2Label?: string;
    offer3Label?: string;
    offer1ExtraPrice?: string;
    offer2ExtraPrice?: string;
    offer3ExtraPrice?: string;
    [key: string]: string | undefined;
}

interface BuildPlan {
    offer1ExtraPrice: string;
    offer1Label: string;
    offer1PriceText: React.ReactNode;
    offer1Cta: ButtonProps;
    offer1Channels: OfferChannels;

    offer2ExtraPrice: string;
    offer2Label: string;
    offer2PriceText: React.ReactNode;
    offer2Cta: ButtonProps;
    offer2Channels: OfferChannels;

    offer3ExtraPrice: string;
    offer3Label: string;
    offer3PriceText: React.ReactNode;
    offer3Cta: ButtonProps;
    offer3Channels: OfferChannels;

    offer12PriceText: React.ReactNode;
    offer12Cta: ButtonProps;
    offer12Channels: OfferChannels;

    offer13PriceText: React.ReactNode;
    offer13Cta: ButtonProps;
    offer13Channels: OfferChannels;

    offer23PriceText: React.ReactNode;
    offer23Cta: ButtonProps;
    offer23Channels: OfferChannels;

    all_selected_offer_cta?: ButtonProps;
    none_selected_offer_cta?: ButtonProps;

    addOnLabel?: string;
}

type ListStyle = 'Checkmark' | 'Bullet' | 'None';

export interface PlanCardContent {
    eyebrow: string;
    title: string;
    listeningOptionsLabel?: string;
    description: React.ReactNode;
    planPriceText: React.ReactNode;
    cardFlagColor: string;
    cardFlagText: React.ReactNode;
    planCardChannels: OfferChannels | null;
    cta: ButtonProps | null;
    buildPlan?: BuildPlan;
    hideBanner?: boolean;
    hideBadge?: boolean;
    underCTA?: boolean;
    blurredBackground?: boolean;
    hideShowMore?: boolean;
    listStyle?: ListStyle;
}

type OfferKey = 'offer1' | 'offer2' | 'offer3' | 'offer12' | 'offer13' | 'offer23';

type MessageKey = 'noneSelectedContent' | 'allSelectedContent';

type ValidKey = OfferKey | MessageKey;

type StylesProps = React.CSSProperties & {
    '--flag-background': string;
};

export function PlanCard({ planCardContent, index = 0, allAccessPriceText }: PlanCardProps): React.JSX.Element {
    const [switches, setSwitches] = useState<Record<string, boolean>>({});
    const [selectedItems, setSelectedItems] = useState<Record<string, string>>({});
    const [priceText, setPriceText] = useState<React.ReactNode | null>(planCardContent?.planPriceText);
    const [selectedCta, setSelectedCta] = useState<ButtonProps | null>(planCardContent?.cta);
    const [selectedOfferChannels, setSelectedOfferChannels] = useState<OfferChannels | null>(
        planCardContent?.planCardChannels,
    );
    const [allAccessModalOpen, setAllAccessModalOpen] = useState(false);
    const [popularChannelModalOpen, setPopularChannelModalOpen] = useState(false);
    const [startAnimation, setStartAnimation] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [fadeTiles, setFadeTiles] = useState(false);
    const [showMoreOpen, setShowMoreOpen] = useState(false);
    const modalContainerRef = useRef<HTMLDivElement>(null);
    const scrolableTilesWrapperRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
    const childRef = useRef<ScrollableChannelTilesHandle>(null);
    const hasAnimatedRef = useRef(false);

    const cardFlagStyles: React.CSSProperties = {
        '--flag-background': planCardContent?.cardFlagColor,
    } as StylesProps;

    const getOfferCombinationKey = (switches: Record<string, boolean>) => {
        const selectedNumbers = Object.entries(switches)
            .filter(([, val]) => val)
            .map(([key]) => key.replace('offer', ''))
            .sort();

        if (selectedNumbers.length === 0) return 'noneSelectedContent';
        if (selectedNumbers.length === 3) return 'allSelectedContent';
        return `offer${selectedNumbers.join('')}`;
    };

    const allAccessModalCloseHandler = () => {
        setStartAnimation(false);
        setFadeOut(false);
        setSelectedItems({});
        setSwitches({});
        setAllAccessModalOpen(false);
        setSelectedCta(planCardContent?.cta);
        setPriceText(planCardContent?.planPriceText);
        setSelectedOfferChannels(planCardContent?.planCardChannels);
    };
    const toggleSwitch = (key: string, price: number, addOnName: string) => {
        setFadeTiles(true);
        setTimeout(() => {
            setSwitches((prev) => {
                const isCurrentlyOn = !!prev[key];
                const updatedSwitches = {
                    ...prev,
                    [key]: !isCurrentlyOn,
                };
                setSelectedItems((prevItems) => {
                    const updatedItems = { ...prevItems };
                    if (isCurrentlyOn) {
                        delete updatedItems[key];
                    } else {
                        updatedItems[key] = addOnName;
                    }
                    return updatedItems;
                });
                const selectedKey = getOfferCombinationKey(updatedSwitches) as ValidKey;
                const fields = (planCardContent?.buildPlan as BuildPlan) || {};

                if (selectedKey === 'noneSelectedContent') {
                    setSelectedCta(planCardContent?.cta);
                    setPriceText(planCardContent?.planPriceText);
                    setSelectedOfferChannels(planCardContent?.planCardChannels);
                } else if (selectedKey === 'allSelectedContent') {
                    setSelectedCta({
                        ...selectedCta,
                        href: '',
                        children: <>Get All Access</>,
                        ariaLabel: 'Get All Access',
                        type: 'button',
                        variant: selectedCta?.variant || 'primary',
                    });
                    setPriceText(allAccessPriceText);
                    setSelectedOfferChannels(planCardContent?.planCardChannels);
                } else {
                    const ctaKey = `${selectedKey}Cta` as keyof typeof fields;
                    const priceKey = `${selectedKey}PriceText` as keyof typeof fields;
                    const channelsKey = `${selectedKey}Channels` as keyof typeof fields;
                    setSelectedCta((fields[ctaKey] as ButtonProps) || null);
                    setPriceText((fields[priceKey] as React.ReactNode) || null);
                    setSelectedOfferChannels((fields[channelsKey] as OfferChannels) || null);
                }
                return updatedSwitches;
            });
        }, 100);
    };

    const extractOffers = (fields: BuildPlanOfferKeys) => {
        if (!fields) {
            return [];
        }
        const offers = [];

        for (let i = 1; i <= 3; i++) {
            const labelKey = `offer${i}Label`;
            const priceKey = `offer${i}ExtraPrice`;

            const label = fields[labelKey];
            if (label) {
                offers.push({
                    key: `offer${i}`,
                    item: label,
                    pricing: fields[priceKey] || '',
                    addOnPrice: parseFloat((fields[priceKey] || '').replace(/[^0-9.]/g, '')) || 0,
                });
            }
        }

        return offers;
    };
    const offerFields = {
        offer1Label: planCardContent?.buildPlan?.offer1Label,
        offer2Label: planCardContent?.buildPlan?.offer2Label,
        offer3Label: planCardContent?.buildPlan?.offer3Label,
        offer1ExtraPrice: planCardContent?.buildPlan?.offer1ExtraPrice,
        offer2ExtraPrice: planCardContent?.buildPlan?.offer2ExtraPrice,
        offer3ExtraPrice: planCardContent?.buildPlan?.offer3ExtraPrice,
    };
    const offers = extractOffers(offerFields as BuildPlanOfferKeys);

    const handleViewMoreClick = () => {
        setPopularChannelModalOpen(true);
    };

    const handleClosePopularChannelModal = () => {
        setPopularChannelModalOpen(false);
    };

    const handleAllSelectedCtaClick = () => {
        setAllAccessModalOpen(true);
    };

    useEffect(() => {
        if (Object.keys(selectedItems)?.length === 3) {
            const timer = setTimeout(() => {
                setAllAccessModalOpen(true);
            }, 600);

            return () => clearTimeout(timer);
        }
    }, [selectedItems]);

    useEffect(() => {
        const scrolableTilesWrapper = scrolableTilesWrapperRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !hasAnimatedRef.current) {
                    childRef.current?.animateOneTile?.();
                    hasAnimatedRef.current = true;
                }
            },
            { threshold: 0.5 },
        );
        if (scrolableTilesWrapper) {
            observer.observe(scrolableTilesWrapper);
        }
        return () => {
            if (scrolableTilesWrapper) {
                observer.unobserve(scrolableTilesWrapper);
            }
        };
    }, []);

    const selectedValues = Object.values(selectedItems);
    const planCardDataComponentType =
        selectedValues.length === 0
            ? `PlanCard-${planCardContent?.title}`
            : `PlanCard-${planCardContent?.title}-${selectedValues.join('-')}`;
    let modalPageName =
        selectedValues.length === 0 ? planCardContent?.title : `${planCardContent?.title}-${selectedValues.join('-')}`;
    modalPageName = modalPageName.replace(/(\s|:)/g, '').toLowerCase();

    const brightness = colorBrightness(planCardContent?.cardFlagColor);

    const borderStyle: CSSPropsVars = {
        ...(!planCardContent?.blurredBackground && selectedCta?.color && { '--card-border-color': selectedCta?.color }),
    };

    return (
        <div
            className={cn(planCard, {
                [bannerHidden]: planCardContent?.hideBanner,
                [styles.blurredBackground]: planCardContent?.blurredBackground,
            })}
            style={borderStyle}
            key={index}
            ref={modalContainerRef}
            data-componenttype={planCardDataComponentType}
            data-cy="plan-card"
        >
            {!planCardContent?.hideBanner && (
                <div
                    className={cn(planCardFlag, {
                        [whiteText]: brightness < 128,
                    })}
                    style={cardFlagStyles}
                >
                    <Text tag="p">{planCardContent?.cardFlagText}</Text>
                </div>
            )}
            <div className={headingSection}>
                <span className={cardEyebrow}>{planCardContent?.eyebrow}</span>
                <span className={cardHeading}>{planCardContent?.title}</span>
                {planCardContent?.listeningOptionsLabel && !planCardContent?.hideBadge && (
                    <ChannelBadge
                        badgeText={planCardContent?.listeningOptionsLabel}
                        className={cn(badgeStyle, {
                            [styles.blurredBackgroundBadge]: planCardContent?.blurredBackground,
                        })}
                    />
                )}
                <div
                    className={cn(cardDescription, {
                        [styles.truncatedDescription]:
                            !offers?.length && !showMoreOpen && !planCardContent?.hideShowMore,
                        [styles.checkmarkList]:
                            planCardContent?.listStyle === 'Checkmark' && planCardContent?.blurredBackground,
                        [styles.bulletList]:
                            planCardContent?.listStyle === 'Bullet' && planCardContent?.blurredBackground,
                        [styles.noList]: planCardContent?.listStyle === 'None' && planCardContent?.blurredBackground,
                    })}
                >
                    <Text tag="div">{planCardContent?.description}</Text>
                    {!offers?.length && !showMoreOpen && !planCardContent?.hideShowMore && (
                        <div className={styles.descriptionFade} />
                    )}
                </div>
            </div>
            {offers?.length > 0 && (
                <>
                    {planCardContent?.buildPlan?.addOnLabel && (
                        <span className={addOnLabel}>{planCardContent?.buildPlan?.addOnLabel}</span>
                    )}
                    <div
                        className={cn(optionalItemWrapper, {
                            [styles.animatePosition]: startAnimation,
                            [styles.fadeOut]: fadeOut,
                        })}
                    >
                        {offers?.map((op, index) => (
                            <div className={optionalItems} key={index}>
                                <span className={optionalItemHeading}>{op.item}</span>
                                <span className={optionalItemSubheading}>{op.pricing}</span>
                                <div className={toggleContainer}>
                                    <div
                                        className={switchContainer}
                                        ref={(el) => {
                                            buttonRefs.current[index] = el;
                                        }}
                                    >
                                        <button
                                            type="button"
                                            aria-pressed={switches[op.key] ? 'true' : 'false'}
                                            aria-label={`${op.item} is ${switches[op.key] ? 'On' : 'Off'}`}
                                            data-linkname={`${op.item} is ${switches[op.key] ? 'Off' : 'On'}`}
                                            className={cn(switchToggle, {
                                                [switchOn]: switches[op.key],
                                            })}
                                            onClick={() => toggleSwitch(op.key, op.addOnPrice, op.item)}
                                        >
                                            <div className={circle} />
                                        </button>
                                        <StarAnimation animate={switches[op.key]} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {!planCardContent?.hideShowMore && (
                <div className={styles.showMoreWrapper}>
                    <ShowMore
                        id={`plancard-showmore-${index}`}
                        showText="Show more"
                        hideText="Show less"
                        isContentShown={showMoreOpen}
                        onClickHandler={() => setShowMoreOpen((prev) => !prev)}
                        color={'var(--base-contrast, #202020)'}
                    />
                </div>
            )}
            {!planCardContent?.underCTA && (
                <div className={planPriceWrapper}>
                    <Text tag="p">{priceText}</Text>
                </div>
            )}
            <div
                className={cn(styles.buttonWrapper, {
                    [styles.buttonWrapperUnderCTA]: planCardContent?.underCTA,
                })}
            >
                {Object.keys(selectedItems)?.length === 3 && index === 1 ? (
                    <Button
                        {...selectedCta}
                        type="button"
                        variant={selectedCta?.variant || 'primary'}
                        ariaLabel={selectedCta?.ariaLabel}
                        className={styles.button}
                        onClick={handleAllSelectedCtaClick}
                    >
                        {selectedCta?.children}
                    </Button>
                ) : (
                    <Button
                        {...selectedCta}
                        type="button"
                        variant={selectedCta?.variant || 'primary'}
                        ariaLabel={selectedCta?.ariaLabel}
                        className={styles.button}
                    />
                )}
            </div>
            {planCardContent?.underCTA && (
                <div
                    className={cn(planPriceWrapper, {
                        [styles.planPriceWrapperUnderCTA]: planCardContent?.underCTA,
                    })}
                >
                    <Text tag="p">{priceText}</Text>
                </div>
            )}
            <div
                className={cn(styles.expandableContent, {
                    [styles.hiddenContent]: !showMoreOpen && !planCardContent?.hideShowMore,
                })}
            >
                {selectedOfferChannels?.popularChannels && selectedOfferChannels?.popularChannels?.length > 0 && (
                    <div
                        ref={index === 0 ? scrolableTilesWrapperRef : null}
                        className={cn(styles.tilesFadeWrapper, {
                            [styles.tilesFadeOut]: fadeTiles,
                        })}
                        onTransitionEnd={() => setFadeTiles(false)}
                    >
                        <ScrollableChannelTiles
                            items={selectedOfferChannels?.popularChannels || []}
                            extraTileColor={selectedOfferChannels?.extraTileColor}
                            extraTileLabel={selectedOfferChannels?.extraTileLabel}
                            handleViewMoreClick={handleViewMoreClick}
                            ref={childRef}
                        />
                    </div>
                )}

                {selectedOfferChannels?.popularChannels && selectedOfferChannels?.popularChannels?.length > 0 && (
                    <div className={styles.viewMore}>
                        <button onClick={handleViewMoreClick} aria-label={selectedOfferChannels?.channelsLinkLabel}>
                            {selectedOfferChannels?.channelsLinkLabel}
                        </button>
                    </div>
                )}
            </div>

            <Modal
                headline="Looks like you want the complete SiriusXM experience "
                subhead="Subscribe to All Access to get all 3 add-ons—and save."
                ctas={
                    [
                        {
                            ...planCardContent?.buildPlan?.all_selected_offer_cta,
                            variant: planCardContent?.buildPlan?.all_selected_offer_cta?.variant || 'primary',
                            type: 'button',
                        },
                    ] as ButtonProps[]
                }
                variant="plancard"
                open={allAccessModalOpen}
                onClose={allAccessModalCloseHandler}
                containerRef={modalContainerRef}
                dataLocalpagename={'overlay:allaccessmodal'}
            />
            <PopularChannelsModal
                open={popularChannelModalOpen}
                onClose={handleClosePopularChannelModal}
                tabs={selectedOfferChannels?.channels?.tabs || []}
                heading={selectedOfferChannels?.channels?.title}
                dataLocalpagename={`overlay:${modalPageName}`}
            />
        </div>
    );
}
